import { useState, useMemo } from "react"
import { motion } from "framer-motion"
import {
  TrendingUp,
  DollarSign,
  Users,
  Activity,
  AlertTriangle,
  BarChart3,
} from "lucide-react"
import { getStats } from "../../api/dashboard"

interface Scenario {
  // Plan prices (CRC/mes)
  bronzePrice: number
  proPrice: number
  goldPrice: number
  platinumPrice: number
  elitePrice: number
  personalPrice: number   // CRC por sesión (mensual promedio por cliente)
  virtualPrice: number
  // Client counts
  bronzeClients: number
  proClients: number
  goldClients: number
  platinumClients: number
  eliteClients: number
  personalClients: number
  virtualClients: number
  // Costs
  trainerSalaryUSD: number
  rentUSD: number
  marketingUSD: number
  additionalTrainerCRC: number
  additionalTrainerHours: number
  exchangeRate: number
}

// Weekly sessions per plan (for capacity planning)
const PLAN_SESSIONS: Record<string, number> = {
  bronze: 1, pro: 2, gold: 3, platinum: 4, elite: 5, personal_training: 2, virtual: 0,
}

const defaults: Scenario = {
  bronzePrice: 29900,    proPrice: 47900,   goldPrice: 65900,
  platinumPrice: 75900,  elitePrice: 99900, personalPrice: 60000, virtualPrice: 35000,
  bronzeClients: 0,      proClients: 0,     goldClients: 0,
  platinumClients: 0,    eliteClients: 0,   personalClients: 0,  virtualClients: 0,
  trainerSalaryUSD: 1000, rentUSD: 1500,    marketingUSD: 200,
  additionalTrainerCRC: 4500, additionalTrainerHours: 0,
  exchangeRate: 520,
}

const presets: Record<string, Partial<Scenario>> = {
  conservative: {
    bronzeClients: 8, proClients: 4, goldClients: 2,
    platinumClients: 1, eliteClients: 0, personalClients: 2, virtualClients: 1,
  },
  moderate: {
    bronzeClients: 12, proClients: 8, goldClients: 5,
    platinumClients: 3, eliteClients: 1, personalClients: 4, virtualClients: 2,
  },
  optimistic: {
    bronzeClients: 18, proClients: 12, goldClients: 8,
    platinumClients: 5, eliteClients: 2, personalClients: 6, virtualClients: 4,
  },
}

const presetLabels: Record<string, { label: string; color: string }> = {
  conservative: { label: "Conservador", color: "text-warning" },
  moderate:     { label: "Moderado",    color: "text-primary" },
  optimistic:   { label: "Optimista",   color: "text-success" },
}

const PLAN_META = [
  { key: "bronze" as const,   priceKey: "bronzePrice" as const,   clientKey: "bronzeClients" as const,   label: "Bronze",     sublabel: "1 sesión/semana",  color: "text-amber-400"  },
  { key: "pro" as const,      priceKey: "proPrice" as const,      clientKey: "proClients" as const,      label: "Pro",        sublabel: "2 sesiones/semana", color: "text-primary"    },
  { key: "gold" as const,     priceKey: "goldPrice" as const,     clientKey: "goldClients" as const,     label: "Gold",       sublabel: "3 sesiones/semana", color: "text-yellow-400" },
  { key: "platinum" as const, priceKey: "platinumPrice" as const, clientKey: "platinumClients" as const, label: "Platinum",   sublabel: "4 sesiones/semana", color: "text-purple-400" },
  { key: "elite" as const,    priceKey: "elitePrice" as const,    clientKey: "eliteClients" as const,    label: "Elite",      sublabel: "5 sesiones/semana", color: "text-emerald-400"},
  { key: "personal" as const, priceKey: "personalPrice" as const, clientKey: "personalClients" as const, label: "Personal",   sublabel: "Entrenamiento",     color: "text-blue-400"   },
  { key: "virtual" as const,  priceKey: "virtualPrice" as const,  clientKey: "virtualClients" as const,  label: "Virtual",    sublabel: "Mensual",           color: "text-cyan-400"   },
]

function formatCRC(n: number) {
  return new Intl.NumberFormat("es-CR", { style: "currency", currency: "CRC", minimumFractionDigits: 0 }).format(n)
}

function InputField({
  label, value, onChange, unit,
}: {
  label: string; value: number; onChange: (v: number) => void; unit?: string
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-muted">{label}</label>
      <div className="relative">
        {unit && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted">{unit}</span>
        )}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className={`w-full rounded-lg border border-white/10 bg-surface-elevated py-2.5 text-sm text-white focus:border-primary ${unit ? "pl-8 pr-3" : "px-3"}`}
        />
      </div>
    </div>
  )
}

export default function FinancialTab() {
  const [s, setS] = useState<Scenario>(defaults)
  const [preset, setPreset] = useState<string | null>(null)

  function set(key: keyof Scenario, value: number) {
    setS((prev) => ({ ...prev, [key]: value }))
    setPreset(null)
  }

  function applyPreset(name: string) {
    setS((prev) => ({ ...prev, ...presets[name] }))
    setPreset(name)
  }

  async function fromRealData() {
    const stats = await getStats()
    const bp = stats.clients_by_plan ?? {}
    const ap = stats.avg_price_by_plan ?? {}
    setS((prev) => ({
      ...prev,
      bronzeClients:   bp["bronze"]            ?? 0,
      proClients:      bp["pro"]               ?? 0,
      goldClients:     bp["gold"]              ?? 0,
      platinumClients: bp["platinum"]          ?? 0,
      eliteClients:    bp["elite"]             ?? 0,
      personalClients: bp["personal_training"] ?? 0,
      virtualClients:  bp["virtual"]           ?? 0,
      bronzePrice:   ap["bronze"]            ?? prev.bronzePrice,
      proPrice:      ap["pro"]               ?? prev.proPrice,
      goldPrice:     ap["gold"]              ?? prev.goldPrice,
      platinumPrice: ap["platinum"]          ?? prev.platinumPrice,
      elitePrice:    ap["elite"]             ?? prev.elitePrice,
      personalPrice: ap["personal_training"] ?? prev.personalPrice,
      virtualPrice:  ap["virtual"]           ?? prev.virtualPrice,
    }))
    setPreset("real")
  }

  const kpis = useMemo(() => {
    const revenueByPlan = {
      bronze:   s.bronzeClients   * s.bronzePrice,
      pro:      s.proClients      * s.proPrice,
      gold:     s.goldClients     * s.goldPrice,
      platinum: s.platinumClients * s.platinumPrice,
      elite:    s.eliteClients    * s.elitePrice,
      personal: s.personalClients * s.personalPrice,
      virtual:  s.virtualClients  * s.virtualPrice,
    }
    const totalRevenue = Object.values(revenueByPlan).reduce((a, b) => a + b, 0)

    // USD costs converted to CRC
    const costsUSDInCRC = (s.trainerSalaryUSD + s.rentUSD + s.marketingUSD) * s.exchangeRate
    // CRC costs (additional trainer hours)
    const additionalTotal = s.additionalTrainerCRC * s.additionalTrainerHours
    const totalCosts = costsUSDInCRC + additionalTotal

    const netProfit = totalRevenue - totalCosts
    const margin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0
    const totalClients = s.bronzeClients + s.proClients + s.goldClients + s.platinumClients + s.eliteClients + s.personalClients + s.virtualClients
    const weeklySessions =
      s.bronzeClients   * PLAN_SESSIONS.bronze   +
      s.proClients      * PLAN_SESSIONS.pro       +
      s.goldClients     * PLAN_SESSIONS.gold      +
      s.platinumClients * PLAN_SESSIONS.platinum  +
      s.eliteClients    * PLAN_SESSIONS.elite     +
      s.personalClients * PLAN_SESSIONS.personal_training
    const revenuePerClient = totalClients > 0 ? totalRevenue / totalClients : 0

    return {
      totalRevenue, totalCosts, netProfit, margin, totalClients,
      weeklySessions, revenuePerClient, revenueByPlan,
      costsUSDInCRC, additionalTotal,
    }
  }, [s])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2
          className="text-sm font-semibold text-white uppercase tracking-widest mb-1"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Modelo Financiero
        </h2>
        <p className="text-xs text-muted">Proyecciones y análisis de rentabilidad</p>
      </div>

      {/* Scenario Presets */}
      <div className="gradient-card border border-white/10 rounded-xl p-5">
        <h3 className="mb-4 text-sm font-semibold text-white flex items-center gap-2">
          <BarChart3 size={16} className="text-primary" />
          Escenarios
        </h3>
        <div className="flex flex-wrap gap-2">
          {Object.keys(presets).map((p) => {
            const info = presetLabels[p]
            return (
              <button
                key={p}
                onClick={() => applyPreset(p)}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition border ${
                  preset === p
                    ? "bg-primary/10 border-primary text-primary glow-cyan"
                    : "border-white/10 text-muted hover:text-white hover:border-white/20"
                }`}
              >
                <span className={preset === p ? "text-primary" : info.color}>●</span>{" "}
                {info.label}
              </button>
            )
          })}
          <button
            onClick={fromRealData}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition border ${
              preset === "real"
                ? "bg-primary/10 border-primary text-primary"
                : "border-white/10 text-muted hover:text-white hover:border-white/20"
            }`}
          >
            Datos Reales (CRM)
          </button>
        </div>
      </div>

      {/* Client Distribution + Costs */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Clientes por plan */}
        <div className="gradient-card border border-white/10 rounded-xl p-5 space-y-3">
          <h4 className="flex items-center gap-2 text-sm font-semibold text-white">
            <Users size={16} className="text-primary" />
            Clientes por Plan
          </h4>
          {PLAN_META.map(({ priceKey, clientKey, label, sublabel, color }) => (
            <div key={clientKey} className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted">
                  <span className={color}>■</span> {label}
                  <span className="text-muted/60 ml-1">({sublabel})</span>
                </label>
                <input
                  type="number"
                  value={s[clientKey]}
                  onChange={(e) => set(clientKey, Number(e.target.value))}
                  className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white focus:border-primary"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted">Precio (₡/mes)</label>
                <input
                  type="number"
                  value={s[priceKey]}
                  onChange={(e) => set(priceKey, Number(e.target.value))}
                  className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white focus:border-primary"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Gastos Operativos */}
        <div className="gradient-card border border-white/10 rounded-xl p-5 space-y-4">
          <h4 className="flex items-center gap-2 text-sm font-semibold text-white">
            <DollarSign size={16} className="text-primary" />
            Gastos Operativos
          </h4>
          <InputField label="Salario Trainer Principal (USD/mes)" value={s.trainerSalaryUSD} onChange={(v) => set("trainerSalaryUSD", v)} unit="$" />
          <InputField label="Alquiler (USD/mes)" value={s.rentUSD} onChange={(v) => set("rentUSD", v)} unit="$" />
          <InputField label="Marketing (USD/mes)" value={s.marketingUSD} onChange={(v) => set("marketingUSD", v)} unit="$" />
          <div className="border-t border-white/10 pt-4">
            <p className="text-xs text-muted mb-3">Trainer Adicional (CRC)</p>
            <div className="grid grid-cols-2 gap-3">
              <InputField label="Tarifa (₡/hora)" value={s.additionalTrainerCRC} onChange={(v) => set("additionalTrainerCRC", v)} unit="₡" />
              <InputField label="Horas/mes" value={s.additionalTrainerHours} onChange={(v) => set("additionalTrainerHours", v)} />
            </div>
          </div>
          <InputField label="Tipo de Cambio USD → CRC" value={s.exchangeRate} onChange={(v) => set("exchangeRate", v)} />

          {/* Cost breakdown */}
          <div className="rounded-lg bg-surface-elevated border border-white/10 p-3 space-y-1.5 text-xs">
            <div className="flex justify-between text-muted">
              <span>Costos USD en CRC</span>
              <span className="text-white">{formatCRC(kpis.costsUSDInCRC)}</span>
            </div>
            <div className="flex justify-between text-muted">
              <span>Trainer adicional</span>
              <span className="text-white">{formatCRC(kpis.additionalTotal)}</span>
            </div>
            <div className="flex justify-between font-semibold text-warning border-t border-white/10 pt-1.5">
              <span>Total Costos</span>
              <span>{formatCRC(kpis.totalCosts)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Profitability Warning */}
      {kpis.netProfit < 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center gap-3 rounded-lg bg-danger/10 border border-danger/30 px-4 py-3"
        >
          <AlertTriangle size={16} className="text-danger shrink-0" />
          <span className="text-sm text-danger">
            La configuración actual no es rentable. Considera aumentar precios, agregar clientes o reducir costos.
          </span>
        </motion.div>
      )}

      {/* KPI Cards */}
      <div>
        <h3
          className="mb-4 text-sm font-semibold text-white uppercase tracking-widest"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Resultados Proyectados
        </h3>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[
            { label: "Ingresos Mensuales", value: formatCRC(kpis.totalRevenue), icon: TrendingUp, accent: "text-primary" },
            { label: "Costos Totales",     value: formatCRC(kpis.totalCosts),   icon: DollarSign, accent: "text-warning" },
            { label: "Ganancia Neta",      value: formatCRC(kpis.netProfit),    icon: Activity,   accent: kpis.netProfit < 0 ? "text-danger" : "text-success" },
            { label: "Margen de Ganancia", value: `${kpis.margin.toFixed(1)}%`, icon: BarChart3,  accent: kpis.margin < 0 ? "text-danger" : "text-success" },
          ].map((k, i) => (
            <motion.div
              key={k.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className="gradient-card border border-white/10 rounded-xl p-4 border-glow"
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-muted uppercase tracking-wide">{k.label}</p>
                <div className="p-1.5 rounded-lg bg-primary/10">
                  <k.icon size={14} className={k.accent} />
                </div>
              </div>
              <p className={`text-xl font-bold ${k.accent} animate-pulse-glow`}>{k.value}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-3">
          {[
            { label: "Total Clientes",       value: kpis.totalClients },
            { label: "Sesiones/Semana",      value: kpis.weeklySessions },
            { label: "Ingreso por Cliente",  value: formatCRC(kpis.revenuePerClient) },
          ].map((k, i) => (
            <motion.div
              key={k.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 + i * 0.08 }}
              className="gradient-card border border-white/10 rounded-xl p-4 text-center"
            >
              <p className="text-xs text-muted uppercase tracking-wide mb-2">{k.label}</p>
              <p className="text-2xl font-bold text-primary">{k.value}</p>
            </motion.div>
          ))}
        </div>

        {/* Revenue breakdown by plan */}
        <div className="mt-4 gradient-card border border-white/10 rounded-xl p-5">
          <h4 className="mb-3 text-sm font-semibold text-white flex items-center gap-2">
            <BarChart3 size={16} className="text-primary" />
            Ingresos por Plan
          </h4>
          <div className="space-y-2">
            {PLAN_META.map(({ key, clientKey, label, color }) => {
              const rev = kpis.revenueByPlan[key as keyof typeof kpis.revenueByPlan] ?? 0
              const pct = kpis.totalRevenue > 0 ? (rev / kpis.totalRevenue) * 100 : 0
              return (
                <div key={key} className="flex items-center gap-3">
                  <span className={`text-xs w-16 shrink-0 ${color}`}>{label}</span>
                  <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.5 }}
                      className="h-full bg-primary rounded-full"
                    />
                  </div>
                  <span className="text-xs text-muted w-28 text-right shrink-0">
                    {formatCRC(rev)} · {s[clientKey]}c
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
